import { useEffect, useRef, useState } from 'react';
import type { IdentityRuntimeState } from '../runtime/session-state';
import type { PanelIdentityRuntime } from '../runtime/identity-runtime';

/** docs/decisions/2026-11-04-staff-session-one-hour-without-use.md: a window is renewed once this tab has held it five
 * minutes, the age from which the server renews a token (`JWT_RENEW_THRESHOLD`: fifty-five minutes of a sixty-minute
 * token). Use slides the hour forward, at most once every five minutes, so a person is signed out after an hour
 * without using the Panel. */
const RENEWAL_AGE_MS = 5 * 60_000;

/** The last two minutes of a window, where every server renews: one still on the two-minute threshold answers the
 * earlier ask with the same token, so the window is asked once more inside them. */
const LAST_MINUTES_MS = 120_000;

/** True once the session's window has ended on the server's clock (`now`): it can no longer be renewed. */
const windowClosed = (expiresAt: string, now: number): boolean => Date.parse(expiresAt) <= now;

/** The window on screen: since when this tab holds it (the device's clock: only its age is read) and which of its two
 * asks, the one at five minutes and the one in the last two minutes, the activity renewal already made. */
type HeldWindow = { readonly expiresAt: string; readonly since: number; asked: 'aged' | 'last' | null };

/** A renewal is due for a window held five minutes or in its last two minutes (`now` is the server's clock). */
const renewalDue = (held: HeldWindow, now: number): boolean =>
  Date.parse(held.expiresAt) - now <= LAST_MINUTES_MS || Date.now() - held.since >= RENEWAL_AGE_MS;

/** The window `expiresAt` as this tab holds it: a new one is held from now. */
function holdWindow(held: { current: HeldWindow | null }, expiresAt: string): HeldWindow {
  if (held.current?.expiresAt !== expiresAt) held.current = { expiresAt, since: Date.now(), asked: null };
  return held.current;
}

/** docs/decisions/2026-10-17-session-recheck-skipped-within-two-minutes.md: a
 * return to the Panel within two minutes of Identity's last confirmation of the
 * session checks nothing; the Backend still authorizes every request.
 * @internal Exported for the composition specs. */
export const SESSION_RECHECK_SKIP_MS = 120_000;

/** Foreground/reconnect revalidates Identity. The expiry timer only freezes
 * presentation, never asserts server authority.
 *
 * A return to the tab is one foreground event: the browser sends a visible
 * `visibilitychange` and a window `focus` for it, and the first one checks;
 * the next waits until the tab or the window has been left again.
 *
 * D-160 ruling 19, as the decision of 4 November 2026 amends it: foreground
 * *activity* renews too, once the window is due. There is still no background
 * renewal — no timer renews anything, a hidden tab renews nothing, and the
 * activity asks at most twice per window. */
export function useIdentitySession(runtime: PanelIdentityRuntime, state: IdentityRuntimeState) {
  const [online, setOnline] = useState(() => navigator.onLine);
  const [verifying, setVerifying] = useState(false);
  const verification = useRef(0);
  const held = useRef<HeldWindow | null>(null);
  const session = state.kind === 'credential_required' ? state.credential : 'session' in state ? state.session : null;
  const expiresAt = session?.canonical.session.expires_at;

  // First of the effects: a window is held from the moment it is on screen.
  useEffect(() => { if (expiresAt) holdWindow(held, expiresAt); }, [expiresAt]);
  useEffect(() => { void runtime.rehydrateSession(); }, [runtime]);
  useEffect(() => {
    let mounted = true;
    // Left: the tab was hidden or the window lost the focus since the last foreground event.
    let away = true;
    // A return from a real disconnect that could not be checked yet (the tab was hidden): the next foreground checks.
    let owed = false;
    const verificationChanged = (pending: boolean) => {
      // Keep the port's dispatch fence synchronous with browser events; React
      // rendering (and DOM inert) alone cannot guard a captured account port.
      runtime.setInteractionFrozen(!navigator.onLine || pending, navigator.onLine ? 'verification' : 'offline');
      setVerifying(pending);
    };
    runtime.setInteractionFrozen(!navigator.onLine, 'offline');
    /** A return from a real disconnect (`owed`) is always checked. */
    const check = () => {
      if (document.visibilityState === 'hidden' || !navigator.onLine) return;
      const snapshot = runtime.getSnapshot();
      // A focus/reconnect must not erase an unconfirmed logout. Only its
      // explicit retry can establish whether the remote session was closed.
      if (snapshot.kind === 'signed_out' || snapshot.kind === 'membership_selection_required'
        || snapshot.kind === 'unavailable' && snapshot.operation === 'logout'
        || snapshot.kind === 'credential_required' && snapshot.status === 'unavailable' && snapshot.operation === 'logout') { owed = false; verificationChanged(false); return; }
      // A window that already closed while the expiry timer was frozen (iOS
      // suspends a background tab's timers) cannot be renewed: the server
      // refuses an expired bearer, so the expiry boundary appears at once.
      if (snapshot.kind === 'ready' && windowClosed(snapshot.session.canonical.session.expires_at, runtime.serverNow())) { owed = false; verificationChanged(false); runtime.expire(); return; }
      // A return to the tab is use: a window that is due is renewed instead of only checked.
      const renewal = snapshot.kind === 'ready' && renewalDue(holdWindow(held, snapshot.session.canonical.session.expires_at), runtime.serverNow());
      // Nothing to confirm: no fence, no banner, no render.
      if (!owed && !renewal && runtime.sessionConfirmedWithin(SESSION_RECHECK_SKIP_MS)) return;
      owed = false;
      const generation = ++verification.current;
      verificationChanged(true);
      const request = renewal ? runtime.renew() : runtime.rehydrateSession();
      void request.finally(() => { if (mounted && generation === verification.current) verificationChanged(false); });
    };
    const foreground = () => {
      if (document.visibilityState === 'hidden') { away = true; return; }
      if (!away && !owed) return;
      away = false;
      check();
    };
    const leave = () => { away = true; };
    const reconnect = () => { owed = true; setOnline(true); verificationChanged(true); check(); };
    const disconnect = () => { verification.current += 1; setOnline(false); verificationChanged(false); };
    window.addEventListener('focus', foreground);
    window.addEventListener('blur', leave);
    document.addEventListener('visibilitychange', foreground);
    window.addEventListener('online', reconnect);
    window.addEventListener('offline', disconnect);
    return () => {
      mounted = false;
      verification.current += 1;
      runtime.setInteractionFrozen(true);
      window.removeEventListener('focus', foreground);
      window.removeEventListener('blur', leave);
      document.removeEventListener('visibilitychange', foreground);
      window.removeEventListener('online', reconnect);
      window.removeEventListener('offline', disconnect);
    };
  }, [runtime]);

  // The runtime owns the automatic re-check after an outage
  // (docs/decisions/2026-10-06-authority-unavailable-is-not-invalid.md); the
  // shell only watches it while mounted.
  useEffect(() => runtime.watchSessionRecovery(), [runtime]);

  useEffect(() => {
    if ((state.kind !== 'ready' && !(state.kind === 'credential_required' && state.status === 'required')) || !expiresAt) return undefined;
    const timeout = window.setTimeout(runtime.expire, Math.max(0, Date.parse(expiresAt) - runtime.serverNow()));
    return () => window.clearTimeout(timeout);
  }, [expiresAt, runtime, state.kind, state.kind === 'credential_required' ? state.status : null]);

  /* D-160 ruling 19 · QA row 31, as docs/decisions/2026-11-04-staff-session-one-hour-without-use.md amends it.
   * Working inside the tab is the same evidence of presence that a refocus is,
   * so it renews the same way, through the same operation: a waiter who taps,
   * types or scrolls is never asked «Sigues aquí, pero hay que confirmar quién
   * eres»; one who leaves the Panel alone for an hour is.
   *
   * The fences, in order of the guards below: the tab has to be visible and
   * online (a hidden tab renews nothing, and nothing here is driven by a
   * timer); a window that ended expires instead; and the window asks at most
   * twice: once when this tab has held it five minutes, the age from which the
   * server renews it, and once more in its last two minutes, counted on the
   * server's clock, where a server still on the old two-minute threshold
   * renews after answering the first ask with the same token. A successful
   * renewal replaces `expiresAt`: the new window is held from then. A failed
   * renewal leaves the window as it was, so its asks stay spent and the
   * boundary still appears. */
  useEffect(() => {
    if (state.kind !== 'ready' || !expiresAt) return undefined;
    const renew = () => {
      if (document.visibilityState === 'hidden' || !navigator.onLine) return;
      if (windowClosed(expiresAt, runtime.serverNow())) { runtime.expire(); return; }
      const current = holdWindow(held, expiresAt);
      const last = Date.parse(expiresAt) - runtime.serverNow() <= LAST_MINUTES_MS;
      if (last ? current.asked === 'last' : current.asked !== null || !renewalDue(current, runtime.serverNow())) return;
      current.asked = last ? 'last' : 'aged';
      void runtime.renew();
    };
    const events = ['pointerdown', 'keydown', 'touchstart'] as const;
    events.forEach((event) => window.addEventListener(event, renew, { passive: true, capture: true }));
    return () => events.forEach((event) => window.removeEventListener(event, renew, { capture: true }));
  }, [expiresAt, runtime, state.kind]);

  return { online, verifying };
}
